import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAdmin } from './AdminContext';
import { adminApi } from './adminApi';
import { DataTable, EmptyState, PageHeader } from './kit';
import { formatDateTime, formatRelative } from './ui';
import { actionLabel } from './auditLabels';



const CATEGORIES = [
  { value: '', label: 'Tudo' },
  { value: 'personal', label: 'Dados pessoais' },
  { value: 'passwords', label: 'Senhas' },
  { value: 'access', label: 'Acessos' },
  { value: 'queries', label: 'Consultas' },
];

const targetLink = (row) => {
  if (row.targetType === 'user') return <Link to={`/admin/users/${row.targetId}`} onClick={(e) => e.stopPropagation()}>Cliente {row.targetId}</Link>;
  if (row.targetType === 'company') return <Link to={`/admin/companies/${row.targetId}`} onClick={(e) => e.stopPropagation()}>Oficina {row.targetId}</Link>;
  if (row.targetType === 'sale') return `Atendimento ${row.targetId}`;
  if (row.details && row.details.plate) return `Placa ${row.details.plate}`;
  return '—';
};

// Auditoria: tabela com quando, quem, acao, alvo, motivo e resultado; filtros por tipo e falhas.
export default function AuditPage() {
  const { admin } = useAdmin();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('loading');
  const [hasMore, setHasMore] = useState(false);
  const [category, setCategory] = useState('');
  const [onlyFailures, setOnlyFailures] = useState(false);

  const load = useCallback(async (before) => {
    setStatus('loading');
    try {
      const params = {};
      if (before) params.before = before;
      if (category) params.category = category;
      if (onlyFailures) params.result = 'fail';
      const page = await adminApi.audit(params);
      setRows((previous) => (before ? [...previous, ...page] : page));
      setHasMore(page.length >= 50);
      setStatus('done');
    } catch (err) {
      setStatus('error');
    }
  }, [category, onlyFailures]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      key: 'when',
      label: 'Quando',
      width: '150px',
      render: (row) => <time dateTime={row.createdAt} title={formatDateTime(row.createdAt)}>{formatRelative(row.createdAt)}</time>,
    },
    { key: 'who', label: 'Quem', width: '110px', render: (row) => (admin && row.adminId === admin.id ? 'Você' : row.adminId ? `Admin #${row.adminId}` : '—') },
    { key: 'action', label: 'Ação', render: (row) => actionLabel(row.action) },
    { key: 'target', label: 'Alvo', width: '160px', render: targetLink },
    { key: 'reason', label: 'Motivo', render: (row) => (row.details && row.details.reason) || '—' },
    {
      key: 'result',
      label: 'Resultado',
      width: '110px',
      align: 'right',
      render: (row) => (row.result === 'fail' ? <span className='ax-badge is-danger'>Falhou</span> : <span className='ax-muted'>OK</span>),
    },
  ];

  return (
    <div className='ax-page'>
      <PageHeader title='Auditoria' meta='Senhas e códigos nunca entram aqui' />

      <div className='ax-toolbar'>
        <div className='ax-segmented' role='group' aria-label='Tipo de ação'>
          {CATEGORIES.map((option) => (
            <button key={option.value || 'all'} type='button' className={category === option.value ? 'is-active' : ''} aria-pressed={category === option.value} onClick={() => setCategory(option.value)}>
              {option.label}
            </button>
          ))}
        </div>
        <label className='ax-check'>
          <input type='checkbox' checked={onlyFailures} onChange={(e) => setOnlyFailures(e.target.checked)} />
          Só falhas
        </label>
      </div>

      {status === 'error' && <p className='ax-alert' role='alert'>Não foi possível carregar a auditoria.</p>}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={status === 'loading'}
        empty={status === 'done' ? <EmptyState title='Nada com esses filtros' /> : null}
      />

      {hasMore && status === 'done' && (
        <div className='ax-footer'>
          <span />
          <button type='button' className='ax-btn' onClick={() => load(rows[rows.length - 1].id)}>Carregar mais antigas</button>
        </div>
      )}
    </div>
  );
}
